'use client';

import { useRef, useState, MouseEvent, useEffect } from 'react';
import { Box, IconButton, SystemIcon } from '@wishket/design-system';
import { useClickOutside } from '@wishket/yogokit';
import { useRouter } from 'next/navigation';

import CollectionScrapTooltip from '@/features/Collection/CollectionQuickMenu/CollectionQuickMenu.parts';
import {
  useCollectionDetailQuery,
  useCollectionScrapMutation,
} from '@/entities/collection';
import { PATH } from '@/shared/constants';
import { useAuth } from '@/shared/hooks';

interface CollectionScrapButtonProps {
  collectionId: number;
}

const CollectionScrapButton = ({
  collectionId,
}: CollectionScrapButtonProps) => {
  const { isAuth } = useAuth();
  const router = useRouter();
  const scrapTooltipRef = useRef<HTMLDivElement>(null);
  const [isScrapTooltipOpen, setIsScrapTooltipOpen] = useState(false);
  const { mutate: collectionScrap } = useCollectionScrapMutation();
  const { data: collectionDetailData } = useCollectionDetailQuery({
    id: collectionId,
  });
  const [isScrapped, setIsScrapped] = useState(false);

  useEffect(() => {
    setIsScrapped(!!collectionDetailData?.isCollectionScrapped);
  }, [collectionDetailData]);

  const handleToggleScrap = () => {
    if (isAuth) {
      collectionScrap(
        { id: collectionId },
        {
          onSuccess: ({ status }) => {
            if (status === 201) {
              setIsScrapTooltipOpen(true);
              setIsScrapped(true);
            }
            if (status === 204) {
              setIsScrapped(false);
            }
          },
        }
      );
    } else {
      router.push(`${PATH.AUTH_LOGIN}`);
    }
  };

  const closeScrapTooltip = () => {
    setIsScrapTooltipOpen(false);
  };

  const toggleScrapButton = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    handleToggleScrap();
  };

  useClickOutside(scrapTooltipRef, closeScrapTooltip);

  return (
    <Box className="absolute right-[5px] top-[15px] tablet:right-[9px] tablet:top-[15px] ">
      <IconButton
        onClick={toggleScrapButton}
        size="md"
        data-testid="collection-scrap-button"
      >
        <SystemIcon
          name="medium_scrap"
          solid={isScrapped}
          className="text-white"
        />
      </IconButton>
      <CollectionScrapTooltip
        ref={scrapTooltipRef}
        isOpen={isScrapTooltipOpen}
      />
    </Box>
  );
};

export default CollectionScrapButton;
